/**
 * UsersTable component for displaying user list in admin interface
 * Feature: 001-user-role-management (Phase 4 - T064, Phase 5 - T092)
 */
import { UserOrInvitation } from '../../types/user';
import { RefreshCw, Ban, Unlock, Mail, Trash2 } from 'lucide-react';
import { UserRoleSelect } from './UserRoleSelect';

interface UsersTableProps {
  users: UserOrInvitation[];
  onSyncUser: (userId: string) => void;
  onUpdateStatus: (userId: string, status: 'active' | 'blocked') => void;
  onDeleteUser: (userId: string, email: string) => void;
  isLoading?: boolean;
}

export function UsersTable({
  users,
  onSyncUser,
  onUpdateStatus,
  onDeleteUser,
  isLoading = false,
}: UsersTableProps) {
  const getSyncStatusBadge = (status: string) => {
    const badges = {
      invited: 'bg-purple-100 text-purple-800',
      synced: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      manual: 'bg-blue-100 text-blue-800',
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  // Use Clerk's banned status as source of truth
  const isUserBlocked = (user: UserOrInvitation) => {
    return user.is_blocked === true;
  };

  const getRoleBadge = (role: string) => {
    return role === 'admin'
      ? 'bg-purple-100 text-purple-800'
      : 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No users found
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Role
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Sync Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Synced
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      user.sync_status === 'invited' ? 'bg-purple-200' : 'bg-gray-200'
                    }`}>
                      {user.sync_status === 'invited' ? (
                        <Mail className="h-5 w-5 text-purple-600" />
                      ) : (
                        <span className="text-sm font-medium text-gray-600">
                          {user.display_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {user.sync_status === 'invited' ? (
                        <span className="italic text-gray-500">Pending Invitation</span>
                      ) : (
                        user.display_name || 'Unknown'
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {user.clerk_id || 'Not synced'}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{user.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {user.sync_status === 'invited' ? (
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadge(user.role)}`}>
                    {user.role}
                  </span>
                ) : (
                  <UserRoleSelect user={user} />
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSyncStatusBadge(user.sync_status)}`}>
                    {user.sync_status}
                  </span>
                  {isUserBlocked(user) && (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      Blocked
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {user.sync_status === 'invited' ? (
                  user.invited_at ? new Date(user.invited_at).toLocaleDateString() : 'Pending'
                ) : (
                  user.last_synced_at ? new Date(user.last_synced_at).toLocaleDateString() : 'Never'
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                {user.sync_status === 'invited' ? (
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-xs text-gray-500 italic">Awaiting acceptance</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onSyncUser(user.id)}
                      className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                      title="Sync user"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                    {isUserBlocked(user) ? (
                      <button
                        onClick={() => onUpdateStatus(user.id, 'active')}
                        className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded"
                        title="Unblock user"
                      >
                        <Unlock className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => onUpdateStatus(user.id, 'blocked')}
                        className="text-orange-600 hover:text-orange-900 p-1 hover:bg-orange-50 rounded"
                        title="Block user"
                      >
                        <Ban className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onDeleteUser(user.id, user.email)}
                      className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                      title="Delete user"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
